// Visa audit pack — one zip for a date range: a workbook of everything we hold about
// visa holders' work rights over the period (reconstructed as it stood on each day —
// see netlify/functions/_shared/visa-audit.js), plus every grant notice and VEVO result
// PDF behind it. Built in the browser: the server returns the data and a file list, and
// each file is fetched on its own (a single response can't carry them all).
import * as XLSX from 'xlsx';
import JSZip from 'jszip';

const sheet = (rows, empty) => XLSX.utils.json_to_sheet(rows.length ? rows : [{ '': empty }]);

export function buildAuditWorkbook(audit) {
  const wb = XLSX.utils.book_new();
  const P = audit.people;
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
    ['Visa work-rights audit — Hollypaul (PH Bouveng)'],
    ['Period', `${audit.from} to ${audit.to}`],
    ['Generated', new Date(audit.generatedAt).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })],
    ['People', P.length],
    [],
    ['How to read this'],
    ['Fortnight', 'Home Affairs counts any 14-day period starting on a Monday, so every Monday-starting window that overlaps the period is listed.'],
    ['Hours', 'Night sort (clock-in records) plus driving (driver logs, latest submission per day; rejected / superseded excluded).'],
    ['Counted', 'Hours on days the 48h/fortnight limit applied. Hours in a recorded course break are shown separately and not counted; after a recorded course end date the person is unlimited.'],
    ['Work rights', 'Worked out for each day from the visa details on record that day and the latest VEVO check on or before it — not from today\'s record.'],
    ['Before 22 Sep 2026', 'Visa-detail history started on 22 Sep 2026. For earlier dates the earliest details on record are used; those rows are marked "yes" under "Details from before history".'],
    ['Files', 'grant-notices and VEVO results in the zip are named <login>/…'],
  ]), 'Read me');

  XLSX.utils.book_append_sheet(wb, sheet(P.map((p) => ({
    Name: p.name, Login: p.login, Roles: p.roles.join(' + '), 'Active now': p.active ? 'yes' : 'no',
    'Work rights at period end': p.summary.rightAtEnd, 'Basis': p.summary.basisAtEnd,
    'Work rights during period': p.summary.rightsInPeriod,
    'Fortnights capped': p.summary.fortnightsCapped, 'Fortnights over 48h': p.summary.fortnightsOver,
    'Peak counted hours': p.summary.peakCounted == null ? '' : p.summary.peakCounted,
    'VEVO checks in period': p.summary.vevoChecksInPeriod,
    'Last VEVO by period end': p.summary.lastVevoByEnd ? `${p.summary.lastVevoByEnd.checkedOn} ${p.summary.lastVevoByEnd.entitlement} ${p.summary.lastVevoByEnd.subclass}` : 'none',
    'Visa expiry at period end': p.summary.visaExpiryAtEnd,
    'VEVO consent at period end': p.summary.consentAtEnd,
    'Details from before history': p.summary.detailsUnversioned ? 'yes' : '',
  })), 'No visa holders in this period'), 'Summary');

  XLSX.utils.book_append_sheet(wb, sheet(P.flatMap((p) => p.fortnights.map((w) => ({
    Name: p.name, Login: p.login, 'Fortnight from': w.start, 'Fortnight to': w.end,
    'Work rights': w.rights, '48h limit applied': w.capApplies ? 'yes' : 'no',
    'Night sort h': w.sort, 'Driving h': w.drive, 'Total h': w.total,
    'Course break h (not counted)': w.breakHours, 'Counted h': w.counted,
    'Over 48h': w.over ? 'OVER' : '', 'Details from before history': w.detailsFromBeforeVersioning ? 'yes' : '',
  }))), 'No hours in this period'), 'Fortnights');

  XLSX.utils.book_append_sheet(wb, sheet(P.flatMap((p) => (p.visaRecords || []).map((r) => ({
    Name: p.name, Login: p.login, 'On record from': r.recordFrom, 'On record to': r.recordTo, Status: r.status,
    'Grant / transaction no.': r.grantNumber, Expiry: r.expiry, Category: r.category, Subclass: r.subclass,
    'Grant notice file': r.grantNoticeFile, 'Uploaded by': r.uploadedBy,
  }))), 'No visa records'), 'Visa records');

  XLSX.utils.book_append_sheet(wb, sheet(P.flatMap((p) => (p.vevoChecks || []).map((c) => ({
    Name: p.name, Login: p.login, 'Checked on': c.checkedOn, 'Work entitlement': c.entitlement, Subclass: c.subclass,
    'Visa name': c.visaName, Conditions: c.conditions, 'Visa expiry': c.expiry, Applicant: c.applicant,
    Source: c.source, 'Checked by': c.checkedBy, 'PDF in pack': c.pdf, Notes: c.notes,
  }))), 'No VEVO checks in this period'), 'VEVO checks');

  XLSX.utils.book_append_sheet(wb, sheet(P.flatMap((p) => (p.consent || []).map((c) => ({
    Name: p.name, Login: p.login, Date: c.date, Event: c.event, Via: c.via, 'Wording version': c.wordingVersion,
  }))), 'No consent records'), 'Consent');

  XLSX.utils.book_append_sheet(wb, sheet(P.flatMap((p) => (p.detailChanges || []).map((d) => ({
    Name: p.name, Login: p.login, Date: d.date, 'Changed by': d.by, 'Pre-history baseline': d.baseline ? 'yes' : '',
    Category: d.category, Subclass: d.subclass, 'Course level': d.courseLevel, Course: d.courseName, 'Course end': d.courseEnd,
    Conditions: d.conditions, Bridging: d.bridging, 'Study terms': d.studyTerms, Applicant: d.applicant, 'Work restricted': d.workRestricted,
  }))), 'No visa-detail changes in this period'), 'Visa detail changes');

  XLSX.utils.book_append_sheet(wb, sheet(audit.accessLog.map((a) => ({
    When: a.when, Person: a.person, Login: a.login, Document: a.document, Action: a.action, By: a.by,
  })), 'No document access in this period'), 'Access log');
  return wb;
}

const b64ToBytes = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

export async function downloadAuditPack({ from, to, logins, call, onProgress = () => {} }) {
  onProgress('Gathering the audit…');
  const d = await call({ action: 'visaAudit', from, to, logins: logins || undefined });
  if (!d || !d.success) throw new Error(d?.error || 'Could not build the audit');
  const audit = d.audit;
  const zip = new JSZip();
  const wb = buildAuditWorkbook(audit);
  zip.file(`visa-audit_${from}_to_${to}.xlsx`, XLSX.write(wb, { type: 'array', bookType: 'xlsx' }));

  const missing = [];
  for (let i = 0; i < audit.files.length; i += 1) {
    const f = audit.files[i];
    onProgress(`Adding documents ${i + 1} of ${audit.files.length}…`);
    try {
      const r = f.kind === 'grant'
        ? await call({ action: 'getDocFile', id: f.id })
        : await call({ action: 'getVevoFile', driverLogin: f.login, checkId: f.checkId });
      if (!r || !r.success) throw new Error(r?.error || 'not available');
      const name = f.filename.replace(/[^\w./ -]/g, '_');
      const ext = /\.[a-z0-9]{2,4}$/i.test(name) ? '' : (/pdf/.test(r.contentType || '') ? '.pdf' : '.jpg');
      zip.file(name + ext, b64ToBytes(r.base64));
    } catch (e) { missing.push(`${f.filename}: ${e.message}`); }
  }
  if (missing.length) zip.file('MISSING-FILES.txt', missing.join('\n'));
  onProgress('Zipping…');
  const blob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `visa-audit-pack_${from}_to_${to}.zip`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  onProgress(`Done — ${audit.people.length} people, ${audit.files.length - missing.length} documents${missing.length ? `, ${missing.length} could not be added (listed in MISSING-FILES.txt)` : ''}.`);
  return audit;
}
